import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/native';
import { featureFlagAtom, useMe } from '@/atoms/Auth';
import { SccButton } from '@/components/atoms';
import MissionCompletedOverlay from '@/components/MissionCompletedOverlay';
import { ScreenLayout } from '@/components/ScreenLayout';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { TutorialMissionTypeDto, } from '@/generated-sources/openapi';
import { useFormExitConfirm } from '@/hooks/useFormExitConfirm';
import { useInterestedRegionGroupLabelMap } from '@/hooks/useListInterestedRegions';
import { useCompleteUserTutorialMission, useRegisterUserInterestedRegionsAndThemes, useUserTutorialProgress, } from '@/hooks/useUserTutorialProgress';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import FormExitConfirmBottomSheet from '@/modals/FormExitConfirmBottomSheet';
import ToastUtils from '@/utils/ToastUtils';
import InterestedFormField from './components/InterestedFormFields';
import RegionSelectBottomSheet from './components/RegionSelectBottomSheet';
import ThemeSelectBottomSheet from './components/ThemeSelectBottomSheet';
import { TUTORIAL_MISSION_1_DESCRIPTION } from './constants';
import { arraysEqualAsSets, regionsToChips, themesToChips } from './utils';
/**
 * 튜토리얼 미션 1: 관심 지역 + 관심 주제 둘 다 입력하는 화면.
 *
 * 프로필에서 단독 변경하는 경우는 EditInterestedRegionScreen / EditInterestedThemesScreen을
 * 사용한다. 이 화면은 튜토리얼 진입 전용.
 */
export default function InterestedRegionAndThemesFormScreen({ navigation, }) {
    var _a, _b, _c, _d;
    const { userInfo } = useMe();
    const featureFlags = useAtomValue(featureFlagAtom);
    // USER_TUTORIAL feature flag 미대상 사용자가 deeplink 등으로 우회 진입한 경우 broken UX
    // 노출 방지. featureFlags === null (아직 getUserInfo 응답 전 / 익명 유저) 동안은 대기.
    useEffect(() => {
        if (featureFlags && !featureFlags.enabledFlags.has('USER_TUTORIAL')) {
            navigation.goBack();
        }
    }, [featureFlags, navigation]);
    const registerMutation = useRegisterUserInterestedRegionsAndThemes();
    const completeMission = useCompleteUserTutorialMission();
    const { data: progress } = useUserTutorialProgress();
    const wasMissionAlreadyCompleted = ((_a = progress === null || progress === void 0 ? void 0 : progress.missions.find(m => m.missionType ===
        TutorialMissionTypeDto.RegisterInterestedRegionsAndThemes)) === null || _a === void 0 ? void 0 : _a.completedAt) != null;
    // 기존에 등록한 관심 지역/주제가 있으면 그 값으로 prefill.
    // userInfo 가 아직 fetch 전이면 빈 배열 (EditInterestedRegionScreen 과 동일 패턴).
    const initialRegionIds = (_b = userInfo === null || userInfo === void 0 ? void 0 : userInfo.interestedRegionIds) !== null && _b !== void 0 ? _b : [];
    const initialThemes = (_c = userInfo === null || userInfo === void 0 ? void 0 : userInfo.interestedThemes) !== null && _c !== void 0 ? _c : [];
    const [selectedRegionIds, setSelectedRegionIds] = useState(initialRegionIds);
    const [selectedThemes, setSelectedThemes] = useState(initialThemes);
    const [isRegionSheetOpen, setIsRegionSheetOpen] = useState(false);
    const [isThemeSheetOpen, setIsThemeSheetOpen] = useState(false);
    const [showCollected, setShowCollected] = useState(false);
    // mutation 성공 후엔 form exit confirm 우회 (이미 저장됐으므로 dirty 의미 없음).
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const isFormDirty = !arraysEqualAsSets(selectedRegionIds, initialRegionIds) ||
        !arraysEqualAsSets(selectedThemes, initialThemes);
    const formExitConfirm = useFormExitConfirm(action => {
        navigation.dispatch(action);
    }, { enabled: isFormDirty && !hasSubmitted });
    const handleSubmit = useCallback(() => {
        if (selectedRegionIds.length === 0) {
            ToastUtils.show('관심 지역을 1개 이상 선택해주세요.');
            return;
        }
        if (selectedThemes.length === 0) {
            ToastUtils.show('관심 주제를 1개 이상 선택해주세요.');
            return;
        }
        registerMutation.mutate({
            interestedRegionIds: selectedRegionIds,
            interestedThemes: selectedThemes,
        }, {
            onSuccess: () => {
                setHasSubmitted(true);
                // 이 화면은 튜토리얼 미션 1 전용 진입 (라우트 자체가 분리되어 있음).
                // 등록 직후 미션 완료를 명시 호출한다 — 서버는 register API 에서 자동
                // 미션 완료를 더 이상 수행하지 않으므로 이 호출이 없으면 미션이 미완료로 남는다.
                completeMission.mutate({
                    missionType: TutorialMissionTypeDto.RegisterInterestedRegionsAndThemes,
                }, {
                    onSettled: () => {
                        if (!wasMissionAlreadyCompleted) {
                            setShowCollected(true);
                        }
                        else {
                            formExitConfirm.bypass();
                            navigation.popTo('TutorialMission', {
                                scrollResetToken: Date.now(),
                            });
                        }
                    },
                });
            },
        });
    }, [
        selectedRegionIds,
        selectedThemes,
        registerMutation,
        completeMission,
        wasMissionAlreadyCompleted,
        formExitConfirm,
        navigation,
    ]);
    const handleCollectedClose = useCallback(() => {
        // 이 화면은 튜토리얼 미션 1 전용. 미션 완료 팝업 "확인" 시 TutorialMissionScreen
        // 으로 popTo (native-stack v7 popTo 는 stack 에 있는 라우트까지 pop).
        setShowCollected(false);
        formExitConfirm.bypass();
        navigation.popTo('TutorialMission', { scrollResetToken: Date.now() });
    }, [formExitConfirm, navigation]);
    const handleRegionConfirm = useCallback((nextSelectedIds) => {
        setSelectedRegionIds(nextSelectedIds);
        setIsRegionSheetOpen(false);
    }, []);
    const handleThemeConfirm = useCallback((nextSelectedThemes) => {
        setSelectedThemes(nextSelectedThemes);
        setIsThemeSheetOpen(false);
    }, []);
    const regionLabelMap = useInterestedRegionGroupLabelMap();
    const regionChips = regionsToChips(selectedRegionIds, regionLabelMap);
    const themeChips = themesToChips(selectedThemes);
    const canSubmit = selectedRegionIds.length > 0 && selectedThemes.length > 0;
    return (_jsx(ScreenLayout, { isHeaderVisible: true, safeAreaEdges: ['bottom'], children: _jsx(LogParamsProvider, { params: { displaySectionName: 'interested_region_and_themes_form' }, children: _jsxs(Container, { children: [_jsxs(Content, { children: [_jsxs(TitleSection, { children: [_jsx(ScreenTitle, { children: "\uB531 \uB9DE\uB294 \uC815\uBCF4\uB97C \uCD94\uCC9C\uD560\uAC8C\uC694!" }), _jsx(Subtitle, { children: TUTORIAL_MISSION_1_DESCRIPTION })] }), _jsxs(FieldSection, { children: [_jsx(InterestedFormField, { label: "\uAD00\uC2EC \uC9C0\uC5ED", selectedChips: regionChips, placeholder: "\uC5EC\uAE30\uB97C \uD074\uB9AD\uD574\uC11C, \uAD00\uC2EC \uC9C0\uC5ED\uC744 \uC54C\uB824\uC8FC\uC138\uC694", elementName: "interested_region_input", onPress: () => setIsRegionSheetOpen(true) }), _jsx(InterestedFormField, { label: "\uAD00\uC2EC \uC8FC\uC81C", selectedChips: themeChips, placeholder: "\uAD00\uC2EC \uC788\uB294 \uC8FC\uC81C\uB97C \uC54C\uB824\uC8FC\uC138\uC694", elementName: "interested_theme_input", onPress: () => setIsThemeSheetOpen(true) })] })] }), _jsx(BottomBar, { children: _jsx(SccButton, { text: "\uC644\uB8CC", elementName: "interested_region_themes_save_button", onPress: handleSubmit, buttonColor: "brand40", textColor: "white", fontFamily: font.pretendardSemibold, fontSize: 18, height: 56, isDisabled: !canSubmit || registerMutation.isPending, style: { borderRadius: 8 } }) }), _jsx(FormExitConfirmBottomSheet, { isVisible: formExitConfirm.isVisible, onConfirm: formExitConfirm.onConfirm, onCancel: formExitConfirm.onCancel }), _jsx(RegionSelectBottomSheet, { isVisible: isRegionSheetOpen, initialSelectedGroupIds: selectedRegionIds, onConfirm: handleRegionConfirm, onClose: () => setIsRegionSheetOpen(false) }), _jsx(ThemeSelectBottomSheet, { isVisible: isThemeSheetOpen, initialSelectedThemes: selectedThemes, onConfirm: handleThemeConfirm, onClose: () => setIsThemeSheetOpen(false) }), showCollected && (_jsx(MissionCompletedOverlay, { isVisible: true, itemImage: require('@/assets/img/tutorial/mission_complete_img_smartphone.png'), description: `계단뿌셔클럽 앱이 설치된 스마트폰 획득!\n${(_d = userInfo === null || userInfo === void 0 ? void 0 : userInfo.nickname) !== null && _d !== void 0 ? _d : '크러셔'}님 덕분에 장소 찾기가 쉬워졌어요!`, confirmElementName: "tutorial_mission_1_completed_confirm", onClose: handleCollectedClose }))] }) }) }));
}
const Container = styled.View `
  flex: 1;
  background-color: ${color.white};
`;
const Content = styled.View `
  flex: 1;
  padding: 10px 20px 40px;
  gap: 40px;
`;
const TitleSection = styled.View `
  gap: 12px;
`;
const ScreenTitle = styled.Text `
  font-family: ${font.pretendardBold};
  font-size: 24px;
  line-height: 32px;
  color: ${color.gray90v2};
`;
const Subtitle = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.gray70v2};
`;
const FieldSection = styled.View `
  gap: 36px;
`;
const BottomBar = styled.View `
  padding: 16px 20px 24px;
  background-color: ${color.white};
`;
